import React, { useState } from 'react';
import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaTrash, FaDownload } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import Paginate from '../../components/Paginate';
import {
  useGetExpensesQuery,
  useDeleteExpenseMutation,

} from '../../slices/expensesApiSlice';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const ExpenseListScreen = () => {
  const { pageNumber } = useParams() || 1;

  const { data, isLoading, error, refetch } = useGetExpensesQuery({ pageNumber });
  const [deleteExpense, { isLoading: loadingDelete }] = useDeleteExpenseMutation();
  
  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
 
  // Delete an expense
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await deleteExpense(id).unwrap();
        toast.success('Expense deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  // Filter and sort expenses
  const filterAndSortExpenses = () => {
    let filteredExpenses = [...(data?.expenses || [])];

    // Filter by text input
    if (filter) {
      filteredExpenses = filteredExpenses.filter(
        (expense) =>
          expense.account?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.accountName?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.details?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.category?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.subAccount?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.notes?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.username?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filteredExpenses = filteredExpenses.filter((expense) => {
        const expenseDate = new Date(expense.date);
        return expenseDate >= start && expenseDate <= end;
      });
    }

    // Sort expenses
    if (sortField) {
      filteredExpenses.sort((a, b) => {
        if (sortField === 'amount') {
          return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          const aValue = a[sortField]?.toLowerCase() || '';
          const bValue = b[sortField]?.toLowerCase() || '';
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
      });
    }

    return filteredExpenses;
  };

  // Calculate total for date range
  const calculateTotalForDateRange = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const filteredExpenses = data?.expenses?.filter((expense) => {
      const expenseDate = new Date(expense.date);
      return expenseDate >= start && expenseDate <= end;
    }) || [];

    return filteredExpenses.reduce((acc, expense) => acc + expense.amount, 0);
  };

  // Calculate total for all expenses
  const totalAmount = data?.expenses?.reduce((acc, expense) => acc + expense.amount, 0) || 0;

  const downloadExpenseImages = async (expense) => {
  if (!expense.images || expense.images.length === 0) {
    toast.warn("This expense has no images");
    return;
  }

  const zip = new JSZip();
  const folder = zip.folder(`Expense_${expense._id}`);

  const imagePromises = expense.images.map(async (imagePath) => {
    try {
      const normalized = imagePath.replace(/\\/g, "/");
      const response = await fetch(normalized);
      const blob = await response.blob();

      const filename = normalized.split("/").pop();
      folder.file(filename, blob);
    } catch (error) {
      console.error(`Failed to fetch ${imagePath}`, error);
    }
  });

  await Promise.all(imagePromises);

  zip.generateAsync({ type: "blob" }).then((zipFile) => {
    saveAs(zipFile, `Expense_${expense._id}_Images.zip`);
    toast.success("Images downloaded successfully");
  });
};

const downloadAllImages = async () => {
  if (!startDate || !endDate) {
    toast.error("Please select a start and end date first");
    return;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  const filteredExpenses =
    data?.expenses?.filter((expense) => {
      const expenseDate = new Date(expense.date);
      return expenseDate >= start && expenseDate <= end;
    }) || [];

  // Count total images first
  const allImages = filteredExpenses.flatMap((e) => e.images || []);

  if (allImages.length === 0) {
    toast.warn("No images found in the selected date range");
    return;
  }

  setIsDownloading(true);
  setDownloadProgress(0);

  const zip = new JSZip();
  const folder = zip.folder("All_Expenses_Images");

  let processed = 0;

  const imagePromises = filteredExpenses.flatMap((expense) =>
    (expense.images || []).map(async (imagePath) => {
      try {
        const normalized = imagePath.replace(/\\/g, "/");
        const response = await fetch(normalized);
        const blob = await response.blob();

        const filename = normalized.split("/").pop();
        folder.file(filename, blob);
      } catch (error) {
        console.error(`Failed to fetch ${imagePath}`, error);
      }

      processed += 1;
      setDownloadProgress(Math.round((processed / allImages.length) * 100));
    })
  );

  await Promise.all(imagePromises);

  zip.generateAsync({ type: "blob" }).then((zipFile) => {
    saveAs(zipFile, "All_Expenses_Images.zip");

    toast.success(
      `Download complete. ${allImages.length} image${allImages.length !== 1 ? "s" : ""} included.`
    );

    setIsDownloading(false);
    setDownloadProgress(0);
  });
};  
      const exportToExcel = () => {

        const expensesData = data?.expenses?.map((expense) => ({
          ExpenseId: expense._id,
          Date: new Date(expense.date).toLocaleDateString(),
          Account: expense.account,
          AccountName: expense.accountName,
          SubAccount: expense.SubAccount,
          Details: expense.details,
          Amount: `${expense.amount.toFixed(2)}`,
          Category: expense.category,
          Notes: expense.notes,
          Images: expense.images[0],
        }));
      
        const worksheet = XLSX.utils.json_to_sheet(expensesData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Expenses');
      
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const file = new Blob([excelBuffer], { type: 'application/octet-stream' });
      
        saveAs(file, 'Expenses.xlsx');
      };

      const filteredTotalAmount = filterAndSortExpenses().reduce(
  (acc, expense) => acc + expense.amount,
  0
   );

   const exportFilteredToExcel = () => {
  const filtered = filterAndSortExpenses();

  if (filtered.length === 0) {
    toast.warn("No filtered data to export");
    return;
  }

  const expensesData = filtered.map((expense) => ({
    ExpenseId: expense._id,
    Date: new Date(expense.date).toLocaleDateString(),
    Account: expense.account,
    AccountName: expense.accountName,
    SubAccount: expense.subAccount, // FIXED
    Details: expense.details,
    Amount: `${expense.amount.toFixed(2)}`,
    Category: expense.category,
    Notes: expense.notes,
    Username: expense.username || "",
    Images: expense.images?.[0] || "",
  }));

  const worksheet = XLSX.utils.json_to_sheet(expensesData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "FilteredExpenses");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const file = new Blob([excelBuffer], { type: "application/octet-stream" });

  saveAs(file, "Filtered_Expenses.xlsx");
};
  
  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Income and Expense</h1>
        </Col>
        <Col className="text-end">

  <Button
    className="my-3 mx-2"
    onClick={downloadAllImages}
    disabled={isDownloading}
  >
    {isDownloading ? (
      <>
        <span className="spinner-border spinner-border-sm me-2"></span>
        Zipping...
      </>
    ) : (
      <>
        <FaDownload /> Dld All Images
      </>
    )}
  </Button>

  {isDownloading && (
    <div className="my-2" style={{ width: "250px" }}>
      <div className="progress" style={{ height: "8px" }}>
        <div
          className="progress-bar progress-bar-striped progress-bar-animated"
          role="progressbar"
          style={{ width: `${downloadProgress}%` }}
        ></div>
      </div>
      <small>{downloadProgress}%</small>
    </div>
  )}

          <Button className="my-3 mx-2" onClick={exportToExcel}>
          <FaDownload /> Exp to Exl
          </Button>
          <Button className="my-3 mx-2" onClick={exportFilteredToExcel}>
          <FaDownload /> Exp Filt
          </Button>
           
          <Link to='/create/expense' className='btn btn-light my-3'>
            Add Data
          </Link>
          <Link to='/update/expense/excelform' className='btn btn-light my-3'>
          ExcelUpdate
          </Link>
          <Link to='/bulk/update/expense' className='btn btn-light my-3'>
          BulkUpdate
          </Link>
          <Link to='/admin/expensesearchreplace' className='btn btn-light my-3'>
          Search
          </Link>

        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col>
          <h5>Total Amount: {totalAmount.toFixed(2)}</h5>
        </Col>
        <Col>
    <h4>
      Total (Filtered): ${filteredTotalAmount.toFixed(2)}{' '}
      <span className="text-muted">({data?.expenses?.length} total)</span>
    </h4>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by account, name, details, category, subAccount, or notes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={4}>
          <h5>
            Total (Filtered Range): D{calculateTotalForDateRange().toFixed(2)}
          </h5>
        </Col>
      </Row>
  
      
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : data?.expenses?.length > 0 ? (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th onClick={() => handleSort('username')} style={{ cursor: 'pointer' }}>
                User {sortField === 'username' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('accountName')} style={{ cursor: 'pointer' }}>
                Customer {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th onClick={() => handleSort('details')} style={{ cursor: 'pointer' }}>
                  Details {sortField === 'details' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                  Amount {sortField === 'amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                  Category {sortField === 'category' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                  ReceiptN {sortField === 'notes' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th>Images</th>
                <th>Actions</th>
                <th>Dld</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortExpenses().map((expense) => (
                <tr key={expense._id}>
                  <td>{new Date(expense.date).toLocaleDateString()}</td>
                  <td>{expense.username}</td>
                  <td>{expense.accountName}</td>
                  <td>{expense.details}</td>
                  <td>
                    {expense.amount >= 0
                      ? `${expense.amount.toFixed(2)}`
                      : `-${Math.abs(expense.amount).toFixed(2)}`}
                  </td>
                  <td>{expense.category}</td>
                  <td>{expense.notes}</td>
                  
                  <td className="thumbnails">
                    {expense.images?.length > 0 && (
                      <img
                        src={expense.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2" 
                        style={{
                          width: '10px',
                          height: '10px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>

                  <td>
                    
                    <Button
                      as={Link}
                      to={`/admin/expense/${expense._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(expense._id)}
                    >
                      <FaTrash />
                    </Button>
                    
                    <Link
                       className="btn btn-primary btn-sm m-2"
                      to={`/expense/${expense._id}`}
                        role="button"
                    >
                    Details
                   </Link>
                    
                  </td>
                  <td> 
                   <Button variant="info" className="btn-sm" onClick={() => downloadExpenseImages(expense)}>
                      <FaDownload /> Download Images
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Paginate pages={data.pages} page={data.page} isAdmin={true} type="expenselist" />
        </>
      ) : (
        <Message variant="info">No expenses found</Message>
      )}
    </>
  );
};

export default ExpenseListScreen;